/**
 * DEMO-ONLY: sessionStorage-backed mutable basket line items for Presidio mock ICM.
 * Removable with mock.interceptor.presidio.ts and mock-data fixtures.
 *
 * Static fixtures always returned one seeded DGX line — add-to-cart looked like a replace,
 * and qty PATCH responses never stuck. This state is the source of truth after first mutation.
 */
import { getNegotiatedPrice } from './presidio-mock-discovery';
import { PRESIDIO_MOCK_PRODUCT_PRICES } from './presidio-mock-product-prices';
import { getAdminAffiliateQuoteEnrichment } from './presidio-mock-admin-my-account';
import {
  clearPresidioActiveQuoteOnBasket,
  getPresidioActiveQuoteOnBasket,
  getPresidioQuoteEnrichment,
  resolvePresidioInboundQuoteStatus,
  setPresidioActiveQuoteOnBasket,
} from './presidio-mock-quotes';
import { isDemoPriceOnRequestSku } from './presidio-price-on-request-skus';
import {
  getPresidioWarehouseHeldItem,
  isPresidioWarehouseHeldSku,
  isPresidioWarehouseHeldPricingActive,
} from './presidio-mock-warehouse-held-inventory';

export const PRESIDIO_MOCK_BASKET_LINES_KEY = 'presidio-mock-basket-lines';

/** DEMO: drop multi ship-to assignments from session lines (used by defaults=0/1 preview). */
export function clearPresidioMockBasketLineShipTos(): void {
  if (typeof sessionStorage === 'undefined' || !hasPresidioMockBasketLines()) {
    return;
  }
  const lines = readPresidioMockBasketLines().map(line => {
    const { shipToAddress: _removed, ...rest } = line;
    return rest;
  });
  writePresidioMockBasketLines(lines);
}


export interface PresidioMockBasketLine {
  id: string;
  sku: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  /** Multi ship-to assignment (address id). DEMO: set via separate-lineitem. */
  shipToAddress?: string;
  /** DEMO inbound quote: lock qty + keep quoted unit price (MVP 3.2.x). */
  quantityFixed?: boolean;
  quoteId?: string;
  /** DEMO MVP 2.6.3: customer-owned warehouse held stock at $0. */
  warehouseHeld?: boolean;
}

interface MoneyPair {
  gross: { value: number; currency: string };
  net: { value: number; currency: string };
  tax?: { value: number; currency: string };
}

/** DEMO: flat sales-tax estimate for cart/checkout cost summary (Texas-style). */
export const PRESIDIO_MOCK_SALES_TAX_RATE = 0.0825;

const SEED_LINES: PresidioMockBasketLine[] = [
  {
    id: 'li-mock-1',
    sku: 'NV-DGX-H100',
    quantity: 1,
    unit: 'pcs',
    unitPrice: PRESIDIO_MOCK_PRODUCT_PRICES['NV-DGX-H100'] ?? 210000,
  },
];

export function roundPresidioMockMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** DEMO: estimated sales tax on a pre-tax merchandise + shipping base. */
export function calculatePresidioMockSalesTax(taxableAmount: number): number {
  if (!taxableAmount || taxableAmount <= 0) {
    return 0;
  }
  return roundPresidioMockMoney(taxableAmount * PRESIDIO_MOCK_SALES_TAX_RATE);
}

function money(value: number): MoneyPair {
  const rounded = roundPresidioMockMoney(value);
  return {
    gross: { value: rounded, currency: 'USD' },
    net: { value: rounded, currency: 'USD' },
  };
}

/**
 * DEMO: `grandTotal` with tax so `BasketMapper` → `taxTotal` and the cost-summary
 * tax row render. Gross = pre-tax + tax; net = pre-tax; tax = estimated sales tax.
 */
export function moneyWithPresidioMockTax(preTaxTotal: number): MoneyPair {
  const preTax = roundPresidioMockMoney(Math.max(0, preTaxTotal));
  const tax = calculatePresidioMockSalesTax(preTax);
  return {
    gross: { value: roundPresidioMockMoney(preTax + tax), currency: 'USD' },
    net: { value: preTax, currency: 'USD' },
    ...(tax > 0 ? { tax: { value: tax, currency: 'USD' } } : {}),
  };
}

/** Contract / catalog unit price (never warehouse $0). */
function catalogUnitPrice(sku: string): number {
  if (isDemoPriceOnRequestSku(sku)) {
    return 0;
  }
  const negotiated = getNegotiatedPrice(sku);
  if (typeof negotiated === 'number') {
    return negotiated;
  }
  return PRESIDIO_MOCK_PRODUCT_PRICES[sku] ?? 1000;
}

function unitPriceFor(sku: string, opts?: { warehouseHeld?: boolean }): number {
  if (opts?.warehouseHeld && isPresidioWarehouseHeldPricingActive() && isPresidioWarehouseHeldSku(sku)) {
    return 0;
  }
  return catalogUnitPrice(sku);
}

function nextLineId(existing: PresidioMockBasketLine[]): string {
  const used = new Set(existing.map(line => line.id));
  let n = existing.length + 1;
  let id = `li-mock-${n}`;
  while (used.has(id)) {
    n += 1;
    id = `li-mock-${n}`;
  }
  return id;
}

function readJsonBody<T>(body: unknown): T | undefined {
  if (body == null) {
    return undefined;
  }
  if (typeof body === 'string') {
    try {
      return JSON.parse(body) as T;
    } catch {
      return undefined;
    }
  }
  if (typeof body === 'object') {
    return body as T;
  }
  return undefined;
}

export function hasPresidioMockBasketLines(): boolean {
  return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(PRESIDIO_MOCK_BASKET_LINES_KEY) !== null;
}

export function readPresidioMockBasketLines(): PresidioMockBasketLine[] {
  if (typeof sessionStorage === 'undefined') {
    return SEED_LINES.map(line => normalizePorLine({ ...line }));
  }
  const raw = sessionStorage.getItem(PRESIDIO_MOCK_BASKET_LINES_KEY);
  if (raw === null) {
    return SEED_LINES.map(line => normalizePorLine({ ...line }));
  }
  try {
    const parsed = JSON.parse(raw) as PresidioMockBasketLine[];
    if (!Array.isArray(parsed)) {
      return SEED_LINES.map(line => normalizePorLine({ ...line }));
    }
    let changed = false;
    const lines = parsed.map(line => {
      const next = normalizePorLine({ ...line });
      if (next.unitPrice !== line.unitPrice) {
        changed = true;
      }
      return next;
    });
    // Persist normalized POR $0 so mini-cart / checkout stay consistent after refresh
    if (changed) {
      writePresidioMockBasketLines(lines);
    }
    return lines;
  } catch {
    return SEED_LINES.map(line => normalizePorLine({ ...line }));
  }
}

/** MVP 2.4.7 / 2.6.3 — force POR and warehouse-held flags to correct unit prices. */
function normalizePorLine(line: PresidioMockBasketLine): PresidioMockBasketLine {
  if (isDemoPriceOnRequestSku(line.sku) && line.unitPrice !== 0) {
    return { ...line, unitPrice: 0 };
  }
  if (line.warehouseHeld && isPresidioWarehouseHeldPricingActive()) {
    return line.unitPrice === 0 ? line : { ...line, unitPrice: 0 };
  }
  return line;
}

function mergeOrCreateLine(
  lines: PresidioMockBasketLine[],
  partial: Omit<PresidioMockBasketLine, 'id'> & { id?: string },
  mergeSameSku: boolean
): { lines: PresidioMockBasketLine[]; added: PresidioMockBasketLine } {
  const existingIndex =
    mergeSameSku && !partial.quantityFixed
      ? lines.findIndex(
          line =>
            line.sku === partial.sku &&
            !line.quantityFixed &&
            !!line.warehouseHeld === !!partial.warehouseHeld
        )
      : -1;
  if (existingIndex >= 0) {
    const [existing] = lines.splice(existingIndex, 1);
    existing.quantity += partial.quantity;
    existing.unitPrice = partial.unitPrice;
    const next = [existing, ...lines];
    return { lines: next, added: { ...existing } };
  }
  const created: PresidioMockBasketLine = {
    id: partial.id || nextLineId(lines),
    sku: partial.sku,
    quantity: partial.quantity,
    unit: partial.unit,
    unitPrice: partial.unitPrice,
    ...(partial.quantityFixed ? { quantityFixed: true } : {}),
    ...(partial.quoteId ? { quoteId: partial.quoteId } : {}),
    ...(partial.warehouseHeld ? { warehouseHeld: true } : {}),
    ...(partial.shipToAddress ? { shipToAddress: partial.shipToAddress } : {}),
  };
  return { lines: [created, ...lines], added: { ...created } };
}

export function writePresidioMockBasketLines(lines: PresidioMockBasketLine[]): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(PRESIDIO_MOCK_BASKET_LINES_KEY, JSON.stringify(lines));
}

export function clearPresidioMockBasketLines(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(PRESIDIO_MOCK_BASKET_LINES_KEY, JSON.stringify([]));
  clearPresidioActiveQuoteOnBasket();
}

export function resetPresidioMockBasketLines(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(PRESIDIO_MOCK_BASKET_LINES_KEY);
}

export function buildPresidioMockLineItemData(line: PresidioMockBasketLine) {
  const lineTotal = line.unitPrice * line.quantity;
  return {
    id: line.id,
    calculated: true,
    position: 1,
    freeGift: false,
    hiddenGift: false,
    quantity: {
      value: line.quantity,
      unit: line.unit || 'pcs',
    },
    product: line.sku,
    // ICM: quantityFixed → LineItem.editable === false
    ...(line.quantityFixed ? { quantityFixed: true } : {}),
    ...(line.quoteId ? { quote: line.quoteId } : {}),
    ...(line.shipToAddress ? { shipToAddress: line.shipToAddress } : {}),
    pricing: {
      singleBasePrice: money(line.unitPrice),
      undiscountedSingleBasePrice: money(line.unitPrice),
      price: money(lineTotal),
      undiscountedPrice: money(lineTotal),
      shippingTotal: money(0),
      total: money(lineTotal),
    },
  };
}

export function applyPresidioMockBasketLinesToPayload(
  body: unknown,
  options?: { lineItemsIncludeKey?: string }
): unknown {
  if (!body || typeof body !== 'object') {
    return body;
  }
  if (!hasPresidioMockBasketLines() && typeof sessionStorage !== 'undefined') {
    // Keep seeded fixture until the shopper mutates the cart.
    return body;
  }

  const lineItemsKey = options?.lineItemsIncludeKey ?? 'lineItems';
  const lines = readPresidioMockBasketLines();
  const clone = JSON.parse(JSON.stringify(body)) as {
    data?: Record<string, unknown>;
    included?: Record<string, unknown>;
  };
  const data = clone.data ?? {};
  const included = (clone.included ?? {}) as Record<string, unknown>;

  // Exclude price-on-request lines from monetary totals (MVP 2.4.7 / standalone parity)
  const itemTotal = lines
    .filter(line => !isDemoPriceOnRequestSku(line.sku) && line.unitPrice > 0)
    .reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);
  const totalQty = lines.reduce((sum, line) => sum + line.quantity, 0);

  data.lineItems = lines.map(line => line.id);
  data.totalProductQuantity = totalQty;

  const totals = (data.totals as Record<string, unknown>) ?? {};
  const shippingGross =
    ((totals.shippingTotal as { gross?: { value?: number } } | undefined)?.gross?.value ??
      (totals.undiscountedShippingTotal as { gross?: { value?: number } } | undefined)?.gross?.value ??
      0) || 0;
  const existingTax =
    ((totals.grandTotal as { tax?: { value?: number } } | undefined)?.tax?.value ?? 0) || 0;
  totals.itemTotal = money(itemTotal);
  totals.undiscountedItemTotal = money(itemTotal);
  // Preserve freight; only keep tax when the interceptor already finalized estimates
  // (ship-to + payment + shipping method). Otherwise merchandise-only grand total.
  totals.grandTotal =
    existingTax > 0
      ? moneyWithPresidioMockTax(itemTotal + shippingGross)
      : money(itemTotal + shippingGross);
  data.totals = totals;

  const lineMap: Record<string, unknown> = {};
  lines.forEach((line, index) => {
    const item = buildPresidioMockLineItemData(line);
    item.position = index + 1;
    lineMap[line.id] = item;
  });
  included[lineItemsKey] = lineMap;

  clone.data = data;
  clone.included = included;
  return clone;
}

type AddItemBody = {
  product?: string;
  quantity?: { value?: number; unit?: string };
  /** Quoted unit price — when set, overrides catalog/negotiated price. */
  unitPrice?: number;
  quantityFixed?: boolean;
  quoteId?: string;
};

/**
 * DEMO-ONLY: quote id → basket add payloads with quoted unit prices (MVP 3.2.x).
 * Used by POST baskets/{id}/quotes so Add quote to cart mutates session lines.
 */
export const PRESIDIO_MOCK_QUOTE_TO_BASKET_LINES: Record<string, AddItemBody[]> = {
  'MOCK-Q-001': [{ product: 'NV-DGX-H100', quantity: { value: 1, unit: 'pcs' }, unitPrice: 379000 }],
  'MOCK-Q-002': [
    { product: 'NV-L40S-48G', quantity: { value: 1, unit: 'pcs' }, unitPrice: 28000 },
    { product: 'SUPPORT-3YR', quantity: { value: 1, unit: 'pcs' }, unitPrice: 20500 },
  ],
  // Display number aliases — API uses MOCK-Q-*; keep both for defensive demo matching.
  'Q-2001': [{ product: 'NV-DGX-H100', quantity: { value: 1, unit: 'pcs' }, unitPrice: 379000 }],
  'Q-2002': [
    { product: 'NV-L40S-48G', quantity: { value: 1, unit: 'pcs' }, unitPrice: 28000 },
    { product: 'SUPPORT-3YR', quantity: { value: 1, unit: 'pcs' }, unitPrice: 20500 },
  ],
  'MOCK-Q-BETA-001': [{ product: 'CISCO-C9300', quantity: { value: 1, unit: 'pcs' }, unitPrice: 18200 }],
  'MOCK-Q-GAMMA-001': [{ product: 'PRS-WIFI-AP', quantity: { value: 1, unit: 'pcs' }, unitPrice: 9600 }],
};

export type AddPresidioMockQuoteToBasketResult = 'added' | 'unknown' | 'not_eligible' | 'already_in_cart';

function basketContainsQuoteId(quoteId: string): boolean {
  const hasLines = readPresidioMockBasketLines().some(line => line.quoteId === quoteId);
  if (hasLines) {
    return true;
  }
  // Stale active-quote key (no matching lines) must not block a real Add to cart.
  if (getPresidioActiveQuoteOnBasket() === quoteId) {
    clearPresidioActiveQuoteOnBasket();
  }
  return false;
}

/**
 * DEMO-ONLY: merge known quote lines into the session basket.
 * Quote lines keep quoted prices and quantityFixed (qty not editable in cart).
 * Same quote cannot be added twice to the same cart; Processing/Expired are ineligible.
 */
export function addPresidioMockQuoteToBasket(quoteId: string): AddPresidioMockQuoteToBasketResult {
  const id = (quoteId || '').trim();
  if (!id) {
    return 'unknown';
  }
  const catalogId = id.startsWith('MOCK-Q-')
    ? id
    : id === 'Q-2001'
      ? 'MOCK-Q-001'
      : id === 'Q-2002'
        ? 'MOCK-Q-002'
        : id;
  const enrichment =
    getPresidioQuoteEnrichment(catalogId) ||
    getPresidioQuoteEnrichment(id) ||
    getAdminAffiliateQuoteEnrichment(catalogId) ||
    getAdminAffiliateQuoteEnrichment(id);
  if (!enrichment) {
    return 'unknown';
  }
  if (resolvePresidioInboundQuoteStatus(enrichment) !== 'Submitted') {
    return 'not_eligible';
  }
  if (basketContainsQuoteId(enrichment.id)) {
    return 'already_in_cart';
  }
  const resolvedId = enrichment.id;
  let lines = PRESIDIO_MOCK_QUOTE_TO_BASKET_LINES[id] || PRESIDIO_MOCK_QUOTE_TO_BASKET_LINES[resolvedId];
  if (!lines?.length && enrichment.lineMeta?.length) {
    const unit =
      enrichment.totalValue && enrichment.lineMeta.length
        ? Math.round(enrichment.totalValue / enrichment.lineMeta.length)
        : undefined;
    lines = enrichment.lineMeta.map(meta => ({
      product: meta.productSKU,
      quantity: { value: 1, unit: 'pcs' },
      unitPrice: unit,
    }));
  }
  if (!lines?.length) {
    return 'unknown';
  }
  // Always create new quote-locked lines (do not merge qty into an existing open SKU).
  addPresidioMockBasketItems(
    lines.map(line => ({
      ...line,
      quantityFixed: true,
      quoteId: resolvedId,
    })),
    { mergeSameSku: false }
  );
  setPresidioActiveQuoteOnBasket(resolvedId);
  return 'added';
}

/**
 * Merge added SKUs into session lines (same SKU increments qty unless mergeSameSku is false).
 * Newest additions are placed first so the mini-cart shows what just landed.
 * Returns the ICM POST items response body.
 */
function heldQtyInCart(lines: PresidioMockBasketLine[], sku: string): number {
  return lines.filter(line => line.sku === sku && line.warehouseHeld).reduce((sum, line) => sum + line.quantity, 0);
}

/**
 * MVP 2.6.3 — warehouse held SKUs add at $0 only up to available owned stock.
 * Quantities above available are ignored (no contract-priced overage).
 */
function applyWarehouseHeldAdd(
  lines: PresidioMockBasketLine[],
  sku: string,
  qty: number,
  unit: string,
  mergeSameSku: boolean
): { lines: PresidioMockBasketLine[]; added: PresidioMockBasketLine[] } {
  const available = getPresidioWarehouseHeldItem(sku)?.availableQty ?? 0;
  const alreadyHeld = heldQtyInCart(lines, sku);
  const heldQty = Math.min(qty, Math.max(0, available - alreadyHeld));
  if (heldQty <= 0) {
    return { lines, added: [] };
  }
  const result = mergeOrCreateLine(
    lines,
    { sku, quantity: heldQty, unit, unitPrice: 0, warehouseHeld: true },
    mergeSameSku
  );
  return { lines: result.lines, added: [result.added] };
}

export function addPresidioMockBasketItems(
  requestBody: unknown,
  options?: { mergeSameSku?: boolean }
): { data: unknown[]; infos: unknown[] } {
  const payload = readJsonBody<AddItemBody[] | AddItemBody>(requestBody);
  const additions = Array.isArray(payload) ? payload : payload ? [payload] : [];
  const mergeSameSku = options?.mergeSameSku !== false;
  let lines = readPresidioMockBasketLines();
  const added: PresidioMockBasketLine[] = [];

  // FR13.7.5: quote-only carts — block catalog adds while any quote line is present.
  const cartHasQuote = lines.some(line => !!line.quoteId);
  const addingNonQuote = additions.some(item => item?.product && !item.quoteId);
  if (cartHasQuote && addingNonQuote) {
    return {
      data: [],
      infos: [
        {
          code: 'ERROR',
          message: 'Quote items are in your cart. Remove the quote before adding other products.',
          causes: [],
        },
      ],
    };
  }

  // Process in reverse so the first requested SKU ends up at the top when unshifting.
  for (const item of [...additions].reverse()) {
    const sku = item?.product;
    if (!sku) {
      continue;
    }
    const qty = Math.max(1, Math.floor(Number(item.quantity?.value ?? 1)));
    const unit = item.quantity?.unit || 'pcs';
    const quotedPrice = typeof item.unitPrice === 'number' ? item.unitPrice : undefined;
    const quantityFixed = !!item.quantityFixed;
    const quoteId = item.quoteId;

    // Warehouse held SKUs: $0 owned stock only, capped at available qty
    if (!quantityFixed && isPresidioWarehouseHeldPricingActive() && isPresidioWarehouseHeldSku(sku)) {
      const heldAdd = applyWarehouseHeldAdd(lines, sku, qty, unit, mergeSameSku);
      lines = heldAdd.lines;
      for (const line of heldAdd.added.reverse()) {
        added.unshift(line);
      }
      continue;
    }

    const result = mergeOrCreateLine(
      lines,
      {
        sku,
        quantity: qty,
        unit,
        unitPrice: quotedPrice ?? unitPriceFor(sku),
        ...(quantityFixed ? { quantityFixed: true } : {}),
        ...(quoteId ? { quoteId } : {}),
      },
      mergeSameSku && !quantityFixed
    );
    lines = result.lines;
    added.unshift(result.added);
  }

  writePresidioMockBasketLines(lines);
  return {
    data: added.map(line => buildPresidioMockLineItemData(lines.find(l => l.id === line.id) ?? line)),
    infos: [],
  };
}

export function updatePresidioMockBasketItem(
  itemId: string,
  requestBody: unknown
): { data: unknown; infos: unknown[] } | undefined {
  const payload = readJsonBody<{
    quantity?: { value?: number; unit?: string };
    product?: string;
    shipToAddress?: string;
  }>(requestBody);
  let lines = readPresidioMockBasketLines();
  const index = lines.findIndex(line => line.id === itemId);
  if (index < 0) {
    return undefined;
  }

  const line = lines[index];
  if (payload?.product && !line.quantityFixed) {
    line.sku = payload.product;
    line.unitPrice = unitPriceFor(payload.product, { warehouseHeld: !!line.warehouseHeld });
  }
  if (payload?.quantity?.value != null) {
    // Quote-locked lines keep the quoted quantity (Add quote to cart).
    if (line.quantityFixed) {
      writePresidioMockBasketLines(lines);
      return { data: buildPresidioMockLineItemData(line), infos: [] };
    }
    const next = Math.floor(Number(payload.quantity.value));
    if (next <= 0) {
      lines.splice(index, 1);
      writePresidioMockBasketLines(lines);
      return { data: buildPresidioMockLineItemData({ ...line, quantity: 0 }), infos: [] };
    }

    // Warehouse held lines cannot exceed available owned stock
    let cappedQty = next;
    if (
      line.warehouseHeld &&
      isPresidioWarehouseHeldPricingActive() &&
      isPresidioWarehouseHeldSku(line.sku)
    ) {
      const available = getPresidioWarehouseHeldItem(line.sku)?.availableQty ?? 0;
      const otherHeld = heldQtyInCart(
        lines.filter(l => l.id !== line.id),
        line.sku
      );
      cappedQty = Math.min(next, Math.max(0, available - otherHeld));
    }

    line.quantity = cappedQty;
    if (payload.quantity.unit) {
      line.unit = payload.quantity.unit;
    }
    if (line.warehouseHeld) {
      line.unitPrice = 0;
    }
  }
  if (payload?.shipToAddress) {
    line.shipToAddress = payload.shipToAddress;
  }

  writePresidioMockBasketLines(lines);
  return { data: buildPresidioMockLineItemData(line), infos: [] };
}

/**
 * DEMO: ICM `POST items/{id}/separate-lineitem` — assign/change multi ship-to on a line.
 * When quantity &lt; current qty, splits into a new line (Amazon-style additional address).
 * Returns `{ data: LineItemData[], infos }` to match MultipleShippingCheckoutService.separateBasketItem.
 */
export function separatePresidioMockBasketItem(
  itemId: string,
  requestBody: unknown
): { data: unknown[]; infos: unknown[] } | undefined {
  const payload = readJsonBody<{
    shipToAddress?: string;
    product?: string;
    quantity?: { value?: number; unit?: string };
  }>(requestBody);

  const lines = readPresidioMockBasketLines();
  const index = lines.findIndex(line => line.id === itemId);
  if (index < 0) {
    return undefined;
  }

  const line = lines[index];
  const splitQty =
    payload.quantity?.value != null ? Math.floor(Number(payload.quantity.value)) : undefined;
  const isPartialSplit =
    splitQty != null && splitQty > 0 && splitQty < (line.quantity ?? 0);

  // Full-line reassignment requires a destination; qty-only split may omit ship-to
  // so the new row opens on “Select a shipping address”.
  if (!isPartialSplit && !payload?.shipToAddress) {
    return undefined;
  }

  // Full-line reassignment (no split qty, or qty covers the whole line).
  if (!isPartialSplit) {
    const updated = updatePresidioMockBasketItem(itemId, {
      shipToAddress: payload.shipToAddress,
      product: payload.product,
      ...(splitQty != null && splitQty > 0
        ? { quantity: { value: splitQty, unit: payload.quantity?.unit || line.unit } }
        : {}),
    });
    if (!updated) {
      return undefined;
    }
    return { data: [updated.data], infos: updated.infos ?? [] };
  }

  // Amazon-style: always create a new shipment row. Omit shipToAddress when the client
  // did not send one so the UI shows the empty “Select a shipping address” placeholder.
  const sku = payload.product || line.sku;
  const unit = payload.quantity?.unit || line.unit;
  line.quantity = (line.quantity ?? 0) - splitQty;
  const { shipToAddress: _donorShipTo, ...lineWithoutShipTo } = line;
  const newLine: PresidioMockBasketLine = {
    ...lineWithoutShipTo,
    id: `MOCK-LI-SPLIT-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    quantity: splitQty,
    unit,
    sku,
    ...(payload.shipToAddress ? { shipToAddress: payload.shipToAddress } : {}),
  };
  lines.push(newLine);
  writePresidioMockBasketLines(lines);
  return {
    data: [buildPresidioMockLineItemData(line), buildPresidioMockLineItemData(newLine)],
    infos: [],
  };
}

export function deletePresidioMockBasketItem(itemId: string): { infos: unknown[] } {
  const before = readPresidioMockBasketLines();
  const removed = before.find(line => line.id === itemId);
  const lines = before.filter(line => line.id !== itemId);
  writePresidioMockBasketLines(lines);
  // If the last line for an active quote was removed, allow Add to cart again.
  if (removed?.quoteId && !lines.some(line => line.quoteId === removed.quoteId)) {
    if (getPresidioActiveQuoteOnBasket() === removed.quoteId) {
      clearPresidioActiveQuoteOnBasket();
    }
  }
  return { infos: [] };
}

export function deleteAllPresidioMockBasketItems(): { infos: unknown[] } {
  clearPresidioMockBasketLines();
  clearPresidioActiveQuoteOnBasket();
  return { infos: [] };
}
